
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ITMGMTLogo } from '@/components/logo/ITMGMTLogo';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const EmailConfirmedPage: React.FC = () => {
  const location = useLocation();
  const { toast } = useToast();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        // Extract token and email from URL parameters
        const searchParams = new URLSearchParams(location.search);
        const token = searchParams.get('token');
        const email = searchParams.get('email');
        
        if (!token) {
          // If no token, show verified page anyway (for cases where they've already verified)
          setVerified(true);
          setVerifying(false);
          return;
        }
        
        if (!email) {
          console.error('Email parameter is missing');
          toast({
            title: 'Verification failed',
            description: 'Email parameter is missing from verification link',
            variant: 'destructive',
          });
          setVerified(false);
          setVerifying(false);
          return;
        }
        
        // Verify the token with Supabase
        const { error } = await supabase.auth.verifyOtp({ 
          email,
          token,
          type: 'signup' 
        });
        
        if (error) {
          console.error('Email verification error:', error);
          toast({
            title: 'Verification failed',
            description: error.message || 'There was a problem verifying your email',
            variant: 'destructive',
          });
          setVerified(false);
        } else {
          setVerified(true);
          toast({
            title: 'Email verified!',
            description: 'Your account has been verified successfully.',
          });
        }
      } catch (error) {
        console.error('Verification error:', error);
        setVerified(false);
      } finally {
        setVerifying(false);
      }
    };
    
    verifyEmail();
  }, [location.search, toast]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-md p-8 text-center">
        <div className="mb-6 flex justify-center">
          <ITMGMTLogo className="h-12" />
        </div>
        
        {verifying ? (
          <div className="py-4">
            <div className="animate-pulse h-16 w-16 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
              <div className="h-8 w-8 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"></div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Verifying Your Email...</h1>
            <p className="text-gray-600">Please wait while we confirm your email address.</p>
          </div>
        ) : verified ? (
          <>
            <svg
              className="w-16 h-16 mx-auto mb-4 text-green-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Email Confirmed!</h1>
            
            <p className="text-gray-600 mb-6">
              Thank you for confirming your email address. Your account has been verified and is now pending approval.
            </p>
          </>
        ) : (
          <>
            <svg
              className="w-16 h-16 mx-auto mb-4 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h1>
            
            <p className="text-gray-600 mb-6">
              We couldn't verify your email address. The link may have expired or been used already.
            </p>
          </>
        )}
        
        <div className="space-y-4">
          <Button asChild className="w-full">
            <Link to="/auth">
              {verified ? "Log In to Your Account" : "Return to Login"}
            </Link>
          </Button>
          
          <p className="text-sm text-gray-500">
            Need help? Contact our{' '}
            <Link to="/contact" className="text-itmgmt-blue hover:underline">
              support team
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailConfirmedPage;
